import React, { useEffect, useState } from "react";
import MasterLayout from "../Sections/MasterLayout";
import { get_contacts, server_post_json } from "../../ServiceConnection/serviceconnection";
import Loader from "./Loader";

export default function ContactsUsList() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  const itemsPerPage = 10; // ← you can change this (5, 10, 20…)

  useEffect(() => {
    fetchContacts();
  }, [currentPage]); // re-fetch when page changes (if backend supports pagination)

  const fetchContacts = async () => {
    try {
      setIsLoading(true);

      // If your backend supports page & limit → pass them
      // For now assuming it returns all (small dataset)
      const response = await server_post_json(get_contacts, {
        page: currentPage,
        limit: itemsPerPage,
      });

      const res = response.data;

      if (res?.success && Array.isArray(res?.data)) {
        setContacts(res.data);

        // Use server pagination if available, otherwise calculate locally
        if (res.pagination?.totalPages) {
          setTotalPages(res.pagination.totalPages);
        } else {
          // fallback — local pagination
          setTotalPages(Math.ceil(res.data.length / itemsPerPage));
        }
      } else {
        setContacts([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side search + filter
  const filteredData = contacts.filter((item) =>
    `${item.name || ""} ${item.email || ""} ${item.phone || ""} ${item.subject || ""} ${item.message || ""}`
      .toLowerCase()
      .includes(search.toLowerCase().trim())
  );

  // If backend doesn't paginate → we paginate locally
  const shouldPaginateLocally = !contacts.length || totalPages === 1;
  const displayData = shouldPaginateLocally
    ? filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredData;

  const currentTotalPages = shouldPaginateLocally
    ? Math.ceil(filteredData.length / itemsPerPage)
    : totalPages;

  const showingFrom = (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, filteredData.length);

  return (
    <MasterLayout>
      {isLoading && <Loader />}

      <div className="bg-white py-6 px-6 mx-auto rounded-xl shadow border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Contact Queries</h2>

          <input
            type="text"
            placeholder="Search by name, email, phone, subject..."
            className="border border-gray-300 px-4 py-2.5 rounded-lg w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">#</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Phone</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Subject</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Message</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-500">
                    {search ? "No matching queries found" : "No contact queries yet"}
                  </td>
                </tr>
              ) : (
                displayData.map((item, index) => (
                  <tr key={item._id} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.name || "—"}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                      {item.email || "—"}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                      {item.phone || item.number || "—"}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {item.subject || "—"}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 max-w-md truncate">
                      {item.message || item.query || "—"}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Summary */}
        {currentTotalPages > 1 && (
          <div className="mt-5 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div>
              Showing <span className="font-medium">{showingFrom}</span> to{" "}
              <span className="font-medium">{showingTo}</span> of{" "}
              <span className="font-medium">{filteredData.length}</span> queries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-md font-medium transition ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Previous
              </button>

              <div className="flex gap-1">
                {[...Array(currentTotalPages)].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-md font-medium transition ${
                        currentPage === page
                          ? "bg-blue-600 text-white"
                          : "bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage((p) => Math.min(currentTotalPages, p + 1))}
                disabled={currentPage === currentTotalPages}
                className={`px-4 py-2 rounded-md font-medium transition ${
                  currentPage === currentTotalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </MasterLayout>
  );
}